import React, { FC, useEffect, useState } from "react";
import {
  Button,
  Card,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import { handleDownloadTemplateFile } from "../..";

import Drawer from "@components/common/Drawer";
import { useGetProcurementSuppliers, useImportProcurementSuppliers } from "@api";
import { ConfirmationModal } from "@components/common";
import { useUserStore } from "@store/user";
import { isValidEmail, isValidDate } from "@helpers";

interface ImportCsv {
  open: boolean;
  onClose: () => void;
}

type SupplierRow = {
  name: string;
  email: string;
  vat: string;
  deadline?: string | null;
  annualProcurementValue?: number | null;
  naceSectionId?: string | null;
  naceDivisionId?: string | null;
};

type InvalidRow = SupplierRow & {
  error: string;
};

const ImportCsv: FC<ImportCsv> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [upload, setUpload] = useState<File>();
  const [invalidRows, setInvalidRows] = useState<InvalidRow[]>([]);

  const [suppliers, setSuppliers] = useState<SupplierRow[]>([]);

  const [isImporting, setIsImporting] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const { importProcurementSuppliersAsync, importProcurementSuppliersEnabled } =
    useImportProcurementSuppliers(user);
  const { procurementSuppliers, procurementSuppliersEnabled } =
    useGetProcurementSuppliers(user);

  const handleImport = async (): Promise<void> => {
    if (suppliers.length) {
      setIsConfirmModalOpen(false);
      setIsImporting(true);
      await importProcurementSuppliersAsync({ suppliers });
      setIsImporting(false);
      handleClose();
    }
  };

  const handleClose = (): void => {
    setUpload(undefined);
    setSuppliers([]);
    setInvalidRows([]);
    onClose();
  };

  const handleBack = (): void => {
    setUpload(undefined);
    setSuppliers([]);
    setInvalidRows([]);
  };

  const handleImportAttempt = (): void => {
    if (!isEmpty(procurementSuppliers?.items)) {
      setIsConfirmModalOpen(true);
    } else {
      handleImport();
    }
  };

  useEffect(() => {
    if (upload) {
      const seenVats = new Set<string>();

      const reader = new FileReader();

      reader.onload = (e): void => {
        const text = e.target?.result as string;
        const delimiter = text.includes(";") ? ";" : ",";
        const rows = text.split("\n").slice(1);

        const suppliers: SupplierRow[] = [];
        const invalidRows: InvalidRow[] = [];

        for (const row of rows) {
          if (!row.trim()) continue;

          const [
            nameRaw,
            emailRaw,
            vatRaw,
            registeredRaw,
            submittedRaw,
            deadlineRaw,
            naceSectionRaw,
            naceDivisionRaw,
            annualProcurementValueRaw,
          ] = row.replace(/"/g, "").split(delimiter);

          const name = nameRaw?.trim();
          const email = emailRaw?.trim().toLowerCase();
          const vat = vatRaw?.trim();
          const deadline = deadlineRaw?.trim();
          const annualProcurementValue = +annualProcurementValueRaw?.trim();
          const naceSectionId = naceSectionRaw?.trim();
          const naceDivisionId = naceDivisionRaw?.trim();

          if (!name || !email || !vat) {
            invalidRows.push({
              name,
              email,
              vat,
              deadline,
              error: t("validation.missingFields"),
            });

            continue;
          }

          if (deadline && !isValidDate(deadline)) {
            invalidRows.push({
              name,
              email,
              vat,
              deadline,
              error: t("validation.invalidDate"),
            });
            continue;
          }

          if (!isValidEmail(email)) {
            invalidRows.push({
              name,
              email,
              vat,
              deadline,
              error: t("validation.invalidEmail"),
            });

            continue;
          }

          if (seenVats.has(vat)) {
            invalidRows.push({
              name,
              email,
              vat,
              deadline,
              error: t("validation.duplicateVat"),
            });

            continue;
          }

          seenVats.add(vat);

          suppliers.push({
            name,
            email,
            vat,
            deadline,
            annualProcurementValue,
            naceSectionId,
            naceDivisionId,
          });
        }

        setInvalidRows(invalidRows);
        setSuppliers(suppliers);
      };

      reader.readAsText(upload, "UTF-8");
    }
  }, [upload]);

  return (
    procurementSuppliersEnabled && (
      <>
        <Drawer
          open={open}
          onClose={onClose}
          content={
            <Stack justifyContent="space-between" height="100%">
              <Stack spacing={4}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={1}
                >
                  <Stack spacing={1}>
                    <FeatherIcon icon="upload" size={24} />
                    <Typography>{t("importCsv.title")}</Typography>
                  </Stack>
                  <IconButton onClick={handleClose}>
                    <FeatherIcon icon="x" size={24} />
                  </IconButton>
                </Stack>
                <Stack height="calc(100vh - 240px)" overflow="auto" gap={4}>
                  {!upload && (
                    <Card variant="outlined">
                      <Stack p={2} spacing={2}>
                        <Typography variant="body1" textAlign="center">
                          {t("importCsv.instructions")}
                        </Typography>
                        <Stack spacing={2} direction="row" justifyContent="center">
                          <Button
                            variant="outlined"
                            startIcon={<FeatherIcon icon="download" size={16} />}
                            onClick={handleDownloadTemplateFile}
                          >
                            {t("importCsv.downloadTemplate")}
                          </Button>
                          <Button
                            variant="contained"
                            component="label"
                            color="primary"
                            startIcon={<FeatherIcon icon="file" size={16} />}
                          >
                            {t("importCsv.uploadCsv")}
                            <input
                              type="file"
                              hidden
                              onChange={(e) => setUpload(e.target.files?.[0])}
                            />
                          </Button>
                        </Stack>
                      </Stack>
                    </Card>
                  )}
                  {upload && (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t("importCsv.table.headerName")}</TableCell>
                            <TableCell>{t("importCsv.table.headerEmail")}</TableCell>
                            <TableCell>{t("importCsv.table.headerVat")}</TableCell>
                            <TableCell>
                              {t("importCsv.table.headerDeadline")}
                            </TableCell>
                            <TableCell>
                              {t("importCsv.table.annualProcurementValue")}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {isEmpty(suppliers) ? (
                            <Typography>{t("importCsv.importError")}</Typography>
                          ) : (
                            suppliers.map((supplier) => (
                              <TableRow
                                key={`Supplier-${supplier.email}-${supplier.vat}`}
                              >
                                <TableCell>{supplier.name}</TableCell>
                                <TableCell>{supplier.email}</TableCell>
                                <TableCell>{supplier.vat}</TableCell>
                                <TableCell>{supplier.deadline}</TableCell>
                                <TableCell>
                                  {supplier.annualProcurementValue}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {!isEmpty(invalidRows) && (
                    <Stack spacing={2}>
                      <Typography color="error">
                        {t("importCsv.invalidRowsTitle")}
                      </Typography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                {t("importCsv.table.headerName")}
                              </TableCell>
                              <TableCell>
                                {t("importCsv.table.headerEmail")}
                              </TableCell>
                              <TableCell>{t("importCsv.table.headerVat")}</TableCell>
                              <TableCell>
                                {t("importCsv.table.headerDeadline")}
                              </TableCell>
                              <TableCell>
                                {t("importCsv.table.annualProcurementValue")}
                              </TableCell>
                              <TableCell>{t("common.reason")}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {invalidRows.map((row) => (
                              <TableRow key={`invalid-${row.email}-${row.vat}`}>
                                <TableCell>{row.name}</TableCell>
                                <TableCell>{row.email}</TableCell>
                                <TableCell>{row.vat}</TableCell>
                                <TableCell>{row.deadline}</TableCell>
                                <TableCell>{row.annualProcurementValue}</TableCell>
                                <TableCell>{row.error}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Stack>
                  )}
                </Stack>
                {upload && importProcurementSuppliersEnabled && (
                  <Stack spacing={2} direction="row" justifyContent="flex-end">
                    <Button variant="outlined" onClick={handleBack}>
                      {t("common.back")}
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        !(upload && !isEmpty(suppliers) && isEmpty(invalidRows))
                      }
                      variant="contained"
                      onClick={handleImportAttempt}
                    >
                      {isImporting ? <CircularProgress /> : t("common.import")}
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Stack>
          }
        />
        <ConfirmationModal
          open={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleImport}
          title={t("importCsv.confirmTitle")}
          description={t("importCsv.confirmDescription")}
          confirmText={t("common.import")}
        />
      </>
    )
  );
};

export default ImportCsv;
